/**
 * What each driver did, and what the page can honestly say about it.
 *
 * Ported from legacy `pages/delivery-statistics.tsx`, with one behaviour change
 * that FB-060 explains: `totalKm` came back as `0.0` for every driver while the
 * per-address distances underneath summed to real numbers, so the total is
 * derived from the addresses when the field is empty.
 *
 * **The server side is fixed** (`MIGRATION-STATUS` FB-060, 2026-08-02):
 * `DriverStatsService.measure` bailed out when the depot could not be geocoded,
 * returning nought instead of summing the distances already recorded on the
 * orders. It now sends 154,8 where the stops sum to 154,81, so the note below
 * stops appearing by itself. The fallback stays — it is the honest reading for
 * any period the server still cannot total, and it costs nothing when it does.
 */

export interface DriverAddress {
  address?: string
  km?: number
}

export interface DriverStat {
  id?: number
  name?: string
  position?: string
  phone?: string
  email?: string
  active?: boolean
  isAvailable?: boolean
  isExternal?: boolean
  vehicleId?: number
  vehicleName?: string
  licensePlate?: string
  deliveryCount?: number
  /** Was nought for every driver before FB-060's server fix — see `kilometresOf`. */
  totalKm?: number
  addresses?: DriverAddress[]
}

/**
 * FB-060. `totalKm` **used to be** `0.0` for every driver the endpoint returned,
 * while their `addresses` carried real per-stop distances — 154,81 km for one
 * driver on a day the field claimed zero.
 *
 * Legacy sums the field, so the page reported zero kilometres driven. Drivers
 * can be paid per kilometre, and this is the screen a manager would check that
 * against, so summing the addresses was the honest reading rather than a
 * behaviour change for its own sake. When the backend populates the field it
 * wins, because it knows about mileage no address list can show.
 *
 * **It populates it now, and this paragraph said otherwise until 2026-08-05.**
 * Measured against the running stack over July: 14 drivers, **none** with a
 * `totalKm` of nought, each within a rounding step of its own stops — 2 987,3
 * reported against 2 987,31 summed. So the branch below is dormant rather than
 * load-bearing, and `isKilometresDerived` answers false for every row, which is
 * why the page's derived-figure note no longer appears.
 *
 * The fallback stays anyway: a period the server cannot total is still worth
 * reading honestly, and it costs nothing on the periods it can.
 */
export function kilometresOf(driver: DriverStat): number {
  const reported = driver.totalKm ?? 0
  if (reported > 0) return reported
  return (driver.addresses ?? []).reduce((total, stop) => total + (stop.km ?? 0), 0)
}

/** Whether any of it is derived, so the page can say so rather than imply precision. */
export function isKilometresDerived(driver: DriverStat): boolean {
  return (driver.totalKm ?? 0) <= 0 && (driver.addresses ?? []).length > 0
}

export interface StatsTotals {
  driverCount: number
  deliveries: number
  kilometres: number
  /** Deliveries per kilometre is meaningless; kilometres per delivery is not. */
  kmPerDelivery: number
}

export function statsTotals(drivers: DriverStat[]): StatsTotals {
  const deliveries = drivers.reduce((total, driver) => total + (driver.deliveryCount ?? 0), 0)
  const kilometres = drivers.reduce((total, driver) => total + kilometresOf(driver), 0)

  return {
    driverCount: drivers.length,
    deliveries,
    kilometres,
    // Guarded: a period with drivers on shift and no deliveries is normal, and
    // dividing by it would print Infinity on a manager's dashboard.
    kmPerDelivery: deliveries > 0 ? kilometres / deliveries : 0,
  }
}

/** Busiest first — the question this page answers is who is carrying the load. */
export function byDeliveries(drivers: DriverStat[]): DriverStat[] {
  return [...drivers].sort(
    (left, right) =>
      (right.deliveryCount ?? 0) - (left.deliveryCount ?? 0) ||
      (left.name ?? '').localeCompare(right.name ?? '', 'sk'),
  )
}

/**
 * Distinct stops, longest first.
 *
 * The endpoint returns one entry per delivery, so the same address appears twice
 * when a driver went back — and the two entries can disagree slightly because
 * the geocoder rounded differently (`Nitrianska 16, Šurany, Slovensko` and
 * `Nitrianska 16, Šurany, 94201` are in the dumps, both 3,73 km). Collapsed on
 * the address string, keeping the count, because "went there twice" is the
 * useful fact.
 */
export interface DriverStop {
  address: string
  km: number
  visits: number
}

export function stopsOf(driver: DriverStat): DriverStop[] {
  const stops = new Map<string, DriverStop>()

  for (const entry of driver.addresses ?? []) {
    const address = entry.address?.trim()
    if (!address) continue
    const existing = stops.get(address)
    if (existing) {
      existing.visits += 1
      continue
    }
    stops.set(address, { address, km: entry.km ?? 0, visits: 1 })
  }

  return [...stops.values()].sort((left, right) => right.km - left.km)
}

/** `Lukas Svajda` → `LS`, for the avatar. */
export function initialsOf(name: string | undefined): string {
  if (!name?.trim()) return '?'
  return (
    name
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}

export function filterDrivers(
  drivers: DriverStat[],
  { driverId, vehicleId }: { driverId: number | null; vehicleId: number | null },
): DriverStat[] {
  return drivers.filter((driver) => {
    if (driverId !== null && driver.id !== driverId) return false
    if (vehicleId !== null && driver.vehicleId !== vehicleId) return false
    return true
  })
}
